import {
  Alert,
  AlertIcon,
  Box,
  ButtonGroup,
  Flex,
  FormControl,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import {
  HiOutlineDocument,
  HiOutlineMicrophone,
  HiOutlineVideoCamera,
  HiOutlineXMark,
} from "react-icons/hi2";

import { AudioIndicator, Button, IconButton, Link } from "../../../components";
import { useVideoRecording } from "../../../hooks/useVideoRecording/useVideoRecording";
import { CATEGORIES } from "../../../utils/googleAnalytics";
import {
  useCreateAiInterviewerIntroMediaUploadMutation,
  useFinalizeAiInterviewerIntroMediaUploadMutation,
  useRemoveAiInterviewerIntroMediaMutation,
} from "../../graphql";
import MonospacedText from "../MonospacedText/MonospacedText";
import {
  ALLOWED_EXTENSIONS,
  ALLOWED_FORMATS,
  buildVideoConstraints,
  formatBytes,
  formatDuration,
  getMediaDurationSeconds,
  getVideoMetadata,
  HARD_MAX_DURATION_SECONDS,
  HARD_MAX_FILE_SIZE_BYTES,
} from "./IntroMediaUpload.utils";
import { MediaFileCard } from "./MediaFileCard";

type IntroMediaUploadProps = {
  aiInterviewerId: string;
  currentMediaUrl?: string | null;
  currentMediaFormat?: string | null;
  currentMediaDurationSeconds?: number | null;
  currentMediaFileName?: string | null;
  hasIntroMedia: boolean;
  readOnly?: boolean;
  onUploadComplete?: () => void;
  onRemoveComplete?: () => void;
};

type UploadState =
  | { type: "empty" }
  | { type: "uploading"; progress: number; uploadSpeed?: number }
  | { type: "processing" }
  | { type: "hardware_setup" }
  | {
      type: "device_setup";
      stream: MediaStream;
      selectedCameraId?: string;
      selectedMicrophoneId?: string;
      audioLevel: number;
    }
  | {
      type: "countdown";
      stream: MediaStream;
      countdown: number;
    }
  | {
      type: "recording";
      duration: number;
      audioLevel: number;
    }
  | {
      type: "preview";
      recording: Blob;
      duration: number;
      previewUrl: string;
    }
  | {
      type: "uploaded";
      url: string;
      format: string;
      durationSeconds: number;
      fileName?: string;
      fileSizeBytes?: number;
    }
  | { type: "error"; message: string; canRetry?: boolean };

type Mode = "upload" | "record";

// Video preview component that doesn't re-render unnecessarily
const VideoPreview = React.memo<{
  stream: MediaStream;
  videoRef: React.RefObject<HTMLVideoElement>;
  onVideoReady?: () => void;
}>(({ stream, videoRef, onVideoReady }) => {
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && stream) {
      videoElement.srcObject = stream;

      // Wait for video to be ready (playing and showing frames)
      const handlePlaying = () => {
        // Small delay to ensure frames are being rendered
        setTimeout(() => {
          onVideoReady?.();
        }, 200);
      };

      const handleLoadedMetadata = () => {
        // Ensure video starts playing
        videoElement.play().catch(() => {
          // Ignore play errors, video will play when user interacts
        });
      };

      videoElement.addEventListener("playing", handlePlaying);
      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);

      return () => {
        videoElement.removeEventListener("playing", handlePlaying);
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
        if (videoElement) {
          videoElement.srcObject = null;
        }
      };
    }
  }, [stream, videoRef, onVideoReady]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  );
});

const IntroMediaUpload: React.FC<IntroMediaUploadProps> = ({
  aiInterviewerId,
  currentMediaUrl,
  currentMediaFormat,
  currentMediaDurationSeconds,
  currentMediaFileName,
  hasIntroMedia,
  readOnly = false,
  onUploadComplete,
  onRemoveComplete,
}) => {
  const [mode, setMode] = useState<Mode>("record");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>(() => {
    if (hasIntroMedia && currentMediaUrl) {
      return {
        type: "uploaded",
        url: currentMediaUrl,
        format: currentMediaFormat || "",
        durationSeconds: currentMediaDurationSeconds || 0,
        fileName: currentMediaFileName || undefined,
      };
    }
    return { type: "empty" };
  });
  const lastUploadedFileNameRef = useRef<string | null>(null);
  const recordingDurationRef = useRef(0);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const discardNextRecordingRef = useRef(false);
  const lastUploadAttemptRef = useRef<{
    recording: Blob;
    fileType: string;
    durationSeconds: number;
    fileName?: string;
    fileSizeBytes?: number;
  } | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewStreamRef = useRef<MediaStream | null>(null);
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const [draggedFile, setDraggedFile] = useState<File | null>(null);

  // Device enumeration and selection
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>(
    []
  );
  const [availableMicrophones, setAvailableMicrophones] = useState<
    MediaDeviceInfo[]
  >([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>("");
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>("");

  // Audio level monitoring
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const [createUpload] = useCreateAiInterviewerIntroMediaUploadMutation();
  const [finalizeUpload] = useFinalizeAiInterviewerIntroMediaUploadMutation();
  const [removeMedia, { loading: isRemoving }] =
    useRemoveAiInterviewerIntroMediaMutation();
  const isUploadingRef = useRef(false);
  const currentUploadXhrRef = useRef<XMLHttpRequest | null>(null);

  // Enumerate available devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");
      const microphones = devices.filter(
        (device) => device.kind === "audioinput"
      );
      setAvailableCameras(cameras);
      setAvailableMicrophones(microphones);

      // Set defaults if not already set
      if (!selectedCameraId && cameras.length > 0) {
        setSelectedCameraId(cameras[0].deviceId);
      }
      if (!selectedMicrophoneId && microphones.length > 0) {
        setSelectedMicrophoneId(microphones[0].deviceId);
      }
    } catch (error) {
      // Failed to enumerate devices
    }
  }, [selectedCameraId, selectedMicrophoneId]);

  // Start audio level monitoring
  const startAudioLevelMonitoring = useCallback((stream: MediaStream) => {
    // Clean up existing monitoring
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }

    try {
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average =
            dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
          const normalizedLevel = Math.min(average / 128, 1); // Normalize to 0-1
          setAudioLevel(normalizedLevel);
        }
      };

      audioLevelIntervalRef.current = setInterval(updateAudioLevel, 50);
    } catch (error) {
      // Failed to start audio level monitoring
    }
  }, []);

  // Stop audio level monitoring
  const stopAudioLevelMonitoring = useCallback(() => {
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  // Consolidated cleanup function for all recording resources
  const cleanupRecordingResources = useCallback(() => {
    // Clean up preview stream
    if (previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach((track) => track.stop());
      previewStreamRef.current = null;
    }
    // Clean up preview URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    // Clear intervals
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    // Stop audio monitoring
    stopAudioLevelMonitoring();
    // Reset duration
    recordingDurationRef.current = 0;
  }, [stopAudioLevelMonitoring]);

  /**
   * Upload intro media directly to GCS using a signed URL.
   * This bypasses the backend server to avoid blocking server threads for large uploads.
   *
   * Flow:
   * 1. Call createAiInterviewerIntroMediaUpload mutation to get a signed PUT URL
   * 2. PUT the file directly to GCS using XHR (for progress tracking)
   * 3. Return the blob name to the caller for finalization
   */
  const uploadIntroMediaToGcs = useCallback(
    async ({
      recording,
      fileType,
      onUploadProgress,
      onUploadCompleted,
      onError,
    }: {
      recording: Blob;
      fileType: string;
      onUploadProgress?(p: number, speed?: number): void;
      onUploadCompleted: (blobName: string) => void;
      onError?: (message: string) => void;
    }) => {
      try {
        // Step 1: Get signed upload URL from backend
        const { data, errors } = await createUpload({
          variables: {
            aiInterviewerId,
            fileType,
          },
        });

        if (errors?.length || !data?.createAiInterviewerIntroMediaUpload) {
          const errorMessage =
            errors?.[0]?.message || "Failed to create upload URL";
          onError?.(errorMessage);
          return;
        }

        const { uploadUrl, blobName } =
          data.createAiInterviewerIntroMediaUpload;

        // Step 2: Upload directly to GCS using XHR for progress tracking
        const xhr = new XMLHttpRequest();
        currentUploadXhrRef.current = xhr;
        let lastLoaded = 0;
        let lastTime = Date.now();

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const now = Date.now();
            const timeDelta = (now - lastTime) / 1000; // seconds
            const loadedDelta = e.loaded - lastLoaded;
            const speed = timeDelta > 0 ? loadedDelta / timeDelta : 0; // bytes per second
            lastLoaded = e.loaded;
            lastTime = now;
            onUploadProgress?.(e.loaded / e.total, speed);
          }
        });

        xhr.addEventListener("error", () => {
          currentUploadXhrRef.current = null;
          onError?.("Upload failed. Please try again");
        });

        xhr.addEventListener("abort", () => {
          currentUploadXhrRef.current = null;
          onError?.("Upload cancelled");
        });

        xhr.addEventListener("load", () => {
          currentUploadXhrRef.current = null;
          // Check if GCS returned success status
          if (xhr.status >= 200 && xhr.status < 300) {
            onUploadCompleted(blobName);
          } else {
            onError?.(
              `Upload failed with status ${xhr.status}. Please try again`
            );
          }
        });

        xhr.open("PUT", uploadUrl, true);
        xhr.send(recording);
      } catch (error) {
        currentUploadXhrRef.current = null;
        onError?.(
          error instanceof Error
            ? error.message
            : "Upload failed. Please try again"
        );
      }
    },
    [aiInterviewerId, createUpload]
  );

  /**
   * Shared upload execution function used by file drop, recording confirmation, and retry.
   * Handles the common upload flow: GCS upload -> finalization -> state updates.
   */
  const executeUpload = useCallback(
    async ({
      recording,
      fileType,
      durationSeconds,
      fileName,
      fileSizeBytes,
      onSuccess,
    }: {
      recording: Blob;
      fileType: string;
      durationSeconds: number;
      fileName?: string;
      fileSizeBytes?: number;
      onSuccess?: () => void;
    }) => {
      isUploadingRef.current = true;
      setUploadState({ type: "uploading", progress: 0 });

      // Store upload attempt for retry
      lastUploadAttemptRef.current = {
        recording,
        fileType,
        durationSeconds,
        fileName,
        fileSizeBytes,
      };

      // Extract video metadata (width, height, bitRate)
      let videoMetadata: {
        width: number;
        height: number;
        bitRate: number;
      } | null = null;
      try {
        videoMetadata = await getVideoMetadata(recording, durationSeconds);
      } catch (error) {
        // If metadata extraction fails, log but continue with upload
        // The backend will accept null values for these fields
        // eslint-disable-next-line no-console
        console.warn("Failed to extract video metadata:", error);
      }

      uploadIntroMediaToGcs({
        recording,
        fileType,
        onUploadProgress(progress, speed) {
          setUploadState({ type: "uploading", progress, uploadSpeed: speed });
        },
        onUploadCompleted(blobName) {
          setUploadState({ type: "processing" });
          finalizeUpload({
            variables: {
              aiInterviewerId,
              blobName,
              durationSeconds,
              bitRate: videoMetadata?.bitRate ?? null,
              height: videoMetadata?.height ?? null,
              width: videoMetadata?.width ?? null,
              fileName: fileName ?? null,
            },
            onCompleted: (finalizeData) => {
              isUploadingRef.current = false;
              if (
                finalizeData?.finalizeAiInterviewerIntroMediaUpload?.success
              ) {
                setUploadState({
                  type: "uploaded",
                  url: currentMediaUrl || "",
                  format: fileType.toUpperCase(),
                  durationSeconds,
                  fileName,
                  fileSizeBytes,
                });
                recordingDurationRef.current = 0;
                // Clean up preview URL
                if (previewUrlRef.current) {
                  URL.revokeObjectURL(previewUrlRef.current);
                  previewUrlRef.current = null;
                }
                lastUploadAttemptRef.current = null;
                onSuccess?.();
                onUploadComplete?.();
              } else {
                setUploadState({
                  type: "error",
                  message:
                    finalizeData?.finalizeAiInterviewerIntroMediaUpload
                      ?.errorMessage || "Upload failed",
                  canRetry: true,
                });
              }
            },
            onError: (error) => {
              isUploadingRef.current = false;
              setUploadState({
                type: "error",
                message: error.message || "Failed to finalize upload",
                canRetry: true,
              });
            },
          });
        },
        onError: (message) => {
          isUploadingRef.current = false;
          setUploadState({
            type: "error",
            message,
            canRetry: true,
          });
        },
      });
    },
    [
      aiInterviewerId,
      finalizeUpload,
      currentMediaUrl,
      onUploadComplete,
      uploadIntroMediaToGcs,
    ]
  );

  const { isRecording, startRecording, stopRecording, stream } =
    useVideoRecording({
      gaEventCategory: "ai_interviewer" as CATEGORIES,
      onRecordingAvailable: (recording: Blob) => {
        if (discardNextRecordingRef.current) {
          discardNextRecordingRef.current = false;
          recordingDurationRef.current = 0;
          return;
        }
        // Stop duration timer
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }

        const duration = recordingDurationRef.current;
        const exceedsHardSize = recording.size > HARD_MAX_FILE_SIZE_BYTES;
        const exceedsHardDuration = duration > HARD_MAX_DURATION_SECONDS;

        if (exceedsHardSize || exceedsHardDuration) {
          const minutes = Math.floor(duration / 60);
          const seconds = duration % 60;
          const durationStr =
            minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
          setUploadState({
            type: "error",
            message: `File exceeds maximum limits: ${formatBytes(
              recording.size
            )} and ${durationStr}. Max ${formatDuration(
              HARD_MAX_DURATION_SECONDS
            )} and ${formatBytes(HARD_MAX_FILE_SIZE_BYTES)}.`,
          });
          recordingDurationRef.current = 0;
          return;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(recording);
        previewUrlRef.current = previewUrl;

        // Show preview instead of immediately uploading
        setUploadState({
          type: "preview",
          recording,
          duration,
          previewUrl,
        });
      },
      onError: (error) => {
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }
        setUploadState({
          type: "error",
          message: error.message || "Recording failed",
        });
        recordingDurationRef.current = 0;
      },
      includeVideo: true,
      includeAudio: true,
      videoConstraints: buildVideoConstraints(selectedCameraId || undefined),
      audioConstraints: selectedMicrophoneId
        ? { deviceId: { exact: selectedMicrophoneId } }
        : true,
    });

  // Retry last upload attempt
  const handleRetryUpload = useCallback(() => {
    if (!lastUploadAttemptRef.current) return;
    const { recording, fileType, durationSeconds, fileSizeBytes, fileName } =
      lastUploadAttemptRef.current;
    executeUpload({
      recording,
      fileType,
      durationSeconds,
      fileName,
      fileSizeBytes,
    });
  }, [executeUpload]);

  // Handle confirming upload from preview
  const handleConfirmUpload = useCallback(
    (recording: Blob, duration: number) => {
      setIsRecordingModalOpen(false);
      lastUploadedFileNameRef.current = null;

      // Determine file type from mime type
      const mimeType = recording.type;
      let fileType = "webm"; // Default for browser recordings
      if (mimeType.includes("mp4")) {
        fileType = "mp4";
      } else if (mimeType.includes("webm")) {
        fileType = "webm";
      } else if (mimeType.includes("quicktime")) {
        fileType = "mov";
      }

      executeUpload({
        recording,
        fileType,
        durationSeconds: duration,
        fileSizeBytes: recording.size,
      });
    },
    [executeUpload]
  );

  // Extract filename from GCS signed URL
  // URL format: https://storage.googleapis.com/{bucket}/{blob_name}?{signature}
  // Blob name format: ai_interviewer_intro/{id}/{filename}.{ext}
  // Sync uploadState with props when they change (but not during upload)
  useEffect(() => {
    if (isUploadingRef.current) {
      // Don't update state during upload
      return;
    }
    if (hasIntroMedia && currentMediaUrl) {
      setUploadState({
        type: "uploaded",
        url: currentMediaUrl,
        format: currentMediaFormat || "",
        durationSeconds: currentMediaDurationSeconds || 0,
        fileName:
          currentMediaFileName || lastUploadedFileNameRef.current || undefined,
        fileSizeBytes: undefined,
      });
    } else if (!hasIntroMedia) {
      setUploadState({ type: "empty" });
      lastUploadedFileNameRef.current = null;
    }
  }, [
    hasIntroMedia,
    currentMediaUrl,
    currentMediaFormat,
    currentMediaDurationSeconds,
    currentMediaFileName,
  ]);

  // Cleanup all recording resources on unmount
  useEffect(() => {
    return () => {
      // Clean up preview stream
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      // Clean up preview URL
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      // Clear intervals
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (readOnly || acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setDraggedFile(null);

      setUploadError(null);

      // Get file extension
      const fileExt = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));
      if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
        setUploadError(
          "Unsupported file format. Supported formats: mp4, mov, avi, mkv, wmv, webm"
        );
        return;
      }

      // Check file size first (before reading duration)
      const exceedsHardSize = file.size > HARD_MAX_FILE_SIZE_BYTES;

      let durationSeconds = 0;
      try {
        durationSeconds = await getMediaDurationSeconds(file);
      } catch (error) {
        setUploadError(
          error instanceof Error
            ? error.message
            : "Unable to determine media duration"
        );
        return;
      }

      // Check both constraints together
      const exceedsHardDuration = durationSeconds > HARD_MAX_DURATION_SECONDS;

      if (exceedsHardSize || exceedsHardDuration) {
        const errors: string[] = [];
        if (exceedsHardSize) {
          errors.push(
            `File exceeds maximum size of ${formatBytes(
              HARD_MAX_FILE_SIZE_BYTES
            )}`
          );
        }
        if (exceedsHardDuration) {
          errors.push(
            `File exceeds maximum length of ${formatDuration(
              HARD_MAX_DURATION_SECONDS
            )}`
          );
        }
        setUploadError(errors.join(". "));
        return;
      }

      // Start upload flow
      lastUploadedFileNameRef.current = null;
      const fileType = fileExt.substring(1); // Remove the dot

      executeUpload({
        recording: file,
        fileType,
        durationSeconds,
        fileName: file.name,
        fileSizeBytes: file.size,
      });
    },
    [readOnly, executeUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (fileRejections: FileRejection[]) => {
      if (readOnly || fileRejections.length === 0) return;

      const rejectedFile = fileRejections[0].file;
      const extension = rejectedFile.name.split(".").pop()?.toLowerCase();
      const rejectedFileType = extension || rejectedFile.type || "unknown";
      setDraggedFile(null);
      setUploadError(`File type (${rejectedFileType}) isn't supported`);
    },
    accept: ALLOWED_FORMATS,
    maxFiles: 1,
    disabled:
      readOnly ||
      uploadState.type === "uploading" ||
      uploadState.type === "processing",
    onDragEnter: (event) => {
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length > 0) {
        setDraggedFile(files[0]);
      }
    },
    onDragOver: (event) => {
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length > 0 && !draggedFile) {
        setDraggedFile(files[0]);
      }
    },
    onDragLeave: () => {
      setDraggedFile(null);
    },
  });

  const handleRemove = useCallback(() => {
    if (readOnly) return;

    removeMedia({
      variables: {
        aiInterviewerId,
      },
      onCompleted: () => {
        setUploadState({ type: "empty" });
        lastUploadedFileNameRef.current = null;
        onRemoveComplete?.();
      },
    });
  }, [readOnly, aiInterviewerId, removeMedia, onRemoveComplete]);

  const handleCancelUpload = useCallback(() => {
    if (readOnly) return;

    // Abort the current upload if in progress
    if (currentUploadXhrRef.current) {
      currentUploadXhrRef.current.abort();
      currentUploadXhrRef.current = null;
    }

    // Reset state
    isUploadingRef.current = false;
    setUploadState({ type: "empty" });
    lastUploadAttemptRef.current = null;
  }, [readOnly]);

  // Request permissions and get preview stream, then transition to device_setup
  const handleRequestPermissions = useCallback(async () => {
    if (readOnly) return;
    try {
      const constraints: MediaStreamConstraints = {
        video: buildVideoConstraints(selectedCameraId || undefined),
        audio: true,
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(
        constraints
      );
      previewStreamRef.current = mediaStream;

      // Enumerate devices after getting permissions
      await enumerateDevices();

      // Start audio level monitoring
      startAudioLevelMonitoring(mediaStream);

      // Get current device IDs from stream
      const videoTrack = mediaStream.getVideoTracks()[0];
      const audioTrack = mediaStream.getAudioTracks()[0];
      const videoDeviceId = videoTrack?.getSettings().deviceId || "";
      const audioDeviceId = audioTrack?.getSettings().deviceId || "";

      if (videoDeviceId) {
        setSelectedCameraId(videoDeviceId);
      }
      if (audioDeviceId) {
        setSelectedMicrophoneId(audioDeviceId);
      }

      setUploadState({
        type: "device_setup",
        stream: mediaStream,
        selectedCameraId: videoDeviceId,
        selectedMicrophoneId: audioDeviceId,
        audioLevel: 0,
      });
    } catch (error) {
      setUploadState({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to access camera/microphone. Please check permissions.",
      });
    }
  }, [readOnly, selectedCameraId, enumerateDevices, startAudioLevelMonitoring]);

  // Handle device selection change
  const handleDeviceChange = useCallback(
    async (deviceType: "camera" | "microphone", deviceId: string) => {
      if (readOnly || uploadState.type !== "device_setup") return;

      // Stop current stream
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      stopAudioLevelMonitoring();

      try {
        const constraints: MediaStreamConstraints = {
          video:
            deviceType === "camera"
              ? buildVideoConstraints(deviceId)
              : uploadState.selectedCameraId
              ? buildVideoConstraints(uploadState.selectedCameraId)
              : buildVideoConstraints(),
          audio:
            deviceType === "microphone"
              ? { deviceId: { exact: deviceId } }
              : uploadState.selectedMicrophoneId
              ? { deviceId: { exact: uploadState.selectedMicrophoneId } }
              : true,
        };

        const newStream = await navigator.mediaDevices.getUserMedia(
          constraints
        );
        previewStreamRef.current = newStream;

        // Start audio level monitoring
        startAudioLevelMonitoring(newStream);

        // Update state with new device selection
        if (deviceType === "camera") {
          setSelectedCameraId(deviceId);
          setUploadState({
            ...uploadState,
            stream: newStream,
            selectedCameraId: deviceId,
          });
        } else {
          setSelectedMicrophoneId(deviceId);
          setUploadState({
            ...uploadState,
            stream: newStream,
            selectedMicrophoneId: deviceId,
          });
        }
      } catch (error) {
        setUploadState({
          type: "error",
          message:
            error instanceof Error ? error.message : "Failed to switch device",
        });
      }
    },
    [readOnly, uploadState, startAudioLevelMonitoring, stopAudioLevelMonitoring]
  );

  const startDurationTimer = useCallback(() => {
    // Start duration timer - update separate state to avoid re-rendering video
    recordingIntervalRef.current = setInterval(() => {
      recordingDurationRef.current += 1;
      setRecordingDuration(recordingDurationRef.current);

      // Auto-stop at 10 minutes
      if (recordingDurationRef.current >= HARD_MAX_DURATION_SECONDS) {
        stopRecording();
      }
    }, 1000);
  }, [stopRecording]);

  // Actually start recording after countdown
  const handleStartActualRecording = useCallback(async () => {
    if (readOnly) return;
    try {
      recordingDurationRef.current = 0;
      setRecordingDuration(0);
      await startRecording();
      // Get current audio level from device_setup state if available
      const currentAudioLevel =
        uploadState.type === "device_setup" ? audioLevel : 0;
      setUploadState({
        type: "recording",
        duration: 0,
        audioLevel: currentAudioLevel,
      });
      startDurationTimer();
    } catch (error) {
      setUploadState({
        type: "error",
        message:
          error instanceof Error ? error.message : "Failed to start recording",
      });
      // Clean up recording resources on error
      cleanupRecordingResources();
    }
  }, [
    readOnly,
    startRecording,
    startDurationTimer,
    uploadState,
    audioLevel,
    cleanupRecordingResources,
  ]);

  // Transition from device_setup to countdown
  const handleStartRecordingFromDeviceSetup = useCallback(() => {
    if (readOnly || uploadState.type !== "device_setup") return;
    const currentStream = uploadState.stream;
    setUploadState({
      type: "countdown",
      stream: currentStream,
      countdown: 3,
    });

    // Start countdown timer
    let currentCountdown = 3;
    countdownIntervalRef.current = setInterval(() => {
      currentCountdown -= 1;
      if (currentCountdown > 0) {
        setUploadState({
          type: "countdown",
          stream: currentStream,
          countdown: currentCountdown,
        });
      } else {
        // Countdown complete, start actual recording
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        handleStartActualRecording();
      }
    }, 1000);
  }, [readOnly, uploadState, handleStartActualRecording]);

  // Check if permissions are already granted
  const checkPermissions = useCallback(async (): Promise<boolean> => {
    if (!navigator.permissions) {
      // Permissions API not supported, need to try getUserMedia
      return false;
    }

    try {
      const checks = [
        navigator.permissions.query({ name: "camera" as PermissionName }),
      ];
      checks.push(
        navigator.permissions.query({ name: "microphone" as PermissionName })
      );

      const results = await Promise.all(checks);
      // If all permissions are granted, we can skip the prompt
      return results.every((result) => result.state === "granted");
    } catch (error) {
      // Permissions API might not support these permission names in some browsers
      // Fall back to trying getUserMedia
      return false;
    }
  }, []);

  // Start hardware setup flow
  const handleStartSetup = useCallback(async () => {
    if (readOnly) return;
    setUploadError(null);
    setIsRecordingModalOpen(true);

    // Check if permissions are already granted
    const hasPermissions = await checkPermissions();

    if (hasPermissions) {
      // Permissions already granted, skip the prompt and request media directly
      await handleRequestPermissions();
    } else {
      // Show the permission prompt UI
      setUploadState({ type: "hardware_setup" });
    }
  }, [readOnly, checkPermissions, handleRequestPermissions]);

  // Start over - skip review and go directly to countdown
  const handleStartOver = useCallback(async () => {
    // Stop current recording if active
    if (isRecording) {
      discardNextRecordingRef.current = true;
      stopRecording();
    }
    // Clean up all recording resources
    cleanupRecordingResources();

    // Request new media stream and go to device_setup, then immediately start countdown
    try {
      const constraints: MediaStreamConstraints = {
        video: buildVideoConstraints(selectedCameraId || undefined),
        audio: selectedMicrophoneId
          ? { deviceId: { exact: selectedMicrophoneId } }
          : true,
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(
        constraints
      );
      previewStreamRef.current = mediaStream;
      startAudioLevelMonitoring(mediaStream);

      // Go directly to countdown
      setUploadState({
        type: "countdown",
        stream: mediaStream,
        countdown: 3,
      });

      // Start countdown timer
      let currentCountdown = 3;
      countdownIntervalRef.current = setInterval(() => {
        currentCountdown -= 1;
        if (currentCountdown > 0) {
          setUploadState({
            type: "countdown",
            stream: mediaStream,
            countdown: currentCountdown,
          });
        } else {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          handleStartActualRecording();
        }
      }, 1000);
    } catch (error) {
      setUploadState({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to access camera/microphone. Please check permissions.",
      });
    }
  }, [
    isRecording,
    stopRecording,
    cleanupRecordingResources,
    selectedCameraId,
    selectedMicrophoneId,
    startAudioLevelMonitoring,
    handleStartActualRecording,
  ]);

  // Exit/cancel recording setup
  const handleExitSetup = useCallback(() => {
    // Stop recording if active
    if (isRecording) {
      discardNextRecordingRef.current = true;
      stopRecording();
    }
    // Clean up all recording resources
    cleanupRecordingResources();
    setUploadState({ type: "empty" });
    setIsRecordingModalOpen(false);
  }, [isRecording, stopRecording, cleanupRecordingResources]);

  const handleStopRecording = useCallback(() => {
    stopRecording();
    // Clean up preview stream (but not the preview URL - that's needed for preview state)
    if (previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach((track) => track.stop());
      previewStreamRef.current = null;
    }
  }, [stopRecording]);

  // Set up video stream for device_setup
  useEffect(() => {
    if (uploadState.type === "device_setup") {
      const videoElement = videoRef.current;
      if (videoElement && uploadState.stream) {
        videoElement.srcObject = uploadState.stream;
        videoElement.play().catch(() => {
          // Ignore play errors
        });
      }
      return () => {
        // Don't clear srcObject here, let VideoPreview handle it
      };
    }
  }, [uploadState.type === "device_setup" ? uploadState.stream : undefined]);

  // Update audio level in recording state
  useEffect(() => {
    if (uploadState.type === "recording" && stream) {
      startAudioLevelMonitoring(stream);
      return () => {
        stopAudioLevelMonitoring();
      };
    }
  }, [
    uploadState.type === "recording",
    stream,
    startAudioLevelMonitoring,
    stopAudioLevelMonitoring,
  ]);

  // Update audio level in device_setup state
  useEffect(() => {
    if (uploadState.type === "device_setup") {
      const interval = setInterval(() => {
        setUploadState((prev) => {
          if (prev.type === "device_setup") {
            return { ...prev, audioLevel };
          }
          return prev;
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [uploadState.type === "device_setup", audioLevel]);

  const uploadedMetaText =
    uploadState.type === "uploaded"
      ? uploadState.fileSizeBytes && uploadState.fileSizeBytes > 0
        ? formatBytes(uploadState.fileSizeBytes)
        : uploadState.durationSeconds > 0
        ? formatDuration(uploadState.durationSeconds)
        : uploadState.format.toUpperCase()
      : null;

  const uploadedFileName =
    uploadState.type === "uploaded"
      ? uploadState.fileName || "Interview Intro Recording"
      : "";

  const errorFileName =
    lastUploadAttemptRef.current?.fileName || "Interview Intro Recording";
  const errorMetaText = lastUploadAttemptRef.current
    ? lastUploadAttemptRef.current.fileSizeBytes &&
      lastUploadAttemptRef.current.fileSizeBytes > 0
      ? formatBytes(lastUploadAttemptRef.current.fileSizeBytes)
      : lastUploadAttemptRef.current.durationSeconds > 0
      ? formatDuration(lastUploadAttemptRef.current.durationSeconds)
      : lastUploadAttemptRef.current.fileType.toUpperCase()
    : null;

  return (
    <FormControl>
      <VStack align="stretch" spacing={2}>
        {uploadState.type === "empty" && (
          <VStack align="stretch" spacing={4}>
            {/* Segmented Control */}
            <Flex justify="center">
              <ButtonGroup
                variant="outline"
                size="xs"
                width="fit-content"
                borderRadius="8px"
                spacing={0}
                gap="4px"
                bg="rgba(0, 0, 0, 0.06)"
                p="4px"
              >
                <Button
                  onClick={() => {
                    if (!readOnly) {
                      setMode("record");
                      setUploadError(null);
                    }
                  }}
                  isDisabled={readOnly}
                  h="28px"
                  px="12px"
                  w="160px"
                  bg={mode === "record" ? "white" : "transparent"}
                  color="gray.900"
                  fontWeight={mode === "record" ? "semibold" : "medium"}
                  fontSize="11.67px"
                  lineHeight="16px"
                  border="none"
                  borderRadius="6px"
                  boxShadow="none"
                  _hover={
                    mode === "record" ? { bg: "white" } : { bg: "transparent" }
                  }
                  _active={{ bg: mode === "record" ? "white" : "transparent" }}
                  _disabled={{
                    bg: "transparent",
                    color: "gray.400",
                    cursor: "not-allowed",
                  }}
                >
                  Record video
                </Button>
                <Button
                  onClick={() => {
                    if (!readOnly) {
                      setMode("upload");
                    }
                  }}
                  isDisabled={readOnly}
                  h="28px"
                  px="12px"
                  w="160px"
                  bg={mode === "upload" ? "white" : "transparent"}
                  color="gray.900"
                  fontWeight={mode === "upload" ? "semibold" : "medium"}
                  fontSize="11.67px"
                  lineHeight="16px"
                  border="none"
                  borderRadius="6px"
                  boxShadow="none"
                  _hover={
                    mode === "upload" ? { bg: "white" } : { bg: "transparent" }
                  }
                  _active={{ bg: mode === "upload" ? "white" : "transparent" }}
                  _disabled={{
                    bg: "transparent",
                    color: "gray.400",
                    cursor: "not-allowed",
                  }}
                >
                  Upload video
                </Button>
              </ButtonGroup>
            </Flex>

            {/* Content based on selected mode */}
            {mode === "record" ? (
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="6px"
                bg="white"
                px={6}
                minH="120px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Button
                  onClick={handleStartSetup}
                  isDisabled={readOnly}
                  bg="white"
                  color="blue.600"
                  borderColor="blue.600"
                  borderWidth="1px"
                  fontSize="sm"
                  borderRadius="6px"
                  _hover={{
                    bg: "blue.50",
                    color: "blue.500",
                    borderColor: "blue.500",
                  }}
                  _active={{
                    bg: "blue.100",
                    color: "blue.400",
                    borderColor: "blue.400",
                  }}
                  _disabled={{
                    bg: "white",
                    color: "gray.400",
                    borderColor: "gray.300",
                    cursor: "not-allowed",
                  }}
                >
                  Record intro
                </Button>
              </Box>
            ) : (
              <VStack spacing={3} align="stretch">
                {uploadError && (
                  <Alert status="error" borderRadius="md" position="relative">
                    <AlertIcon />
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="sm">{uploadError}</Text>
                    </VStack>
                    {!readOnly && (
                      <IconButton
                        aria-label="Close"
                        icon={<HiOutlineXMark />}
                        size="xs"
                        variant="ghost"
                        position="absolute"
                        top="50%"
                        right={2}
                        transform="translateY(-50%)"
                        onClick={() => setUploadError(null)}
                        color="gray.600"
                        _hover={{ bg: "gray.100" }}
                      />
                    )}
                  </Alert>
                )}
                <Box
                  {...getRootProps()}
                  border="2px dashed"
                  borderColor={isDragActive ? "transparent" : "gray.300"}
                  borderRadius="md"
                  py={8}
                  px={6}
                  minH="120px"
                  textAlign="center"
                  cursor={readOnly ? "not-allowed" : "pointer"}
                  bg={isDragActive ? "gray.700" : "white"}
                  _hover={readOnly ? {} : { borderColor: "blue.400" }}
                  position="relative"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <VStack spacing={3} align="center">
                      <Text color="white" fontSize="sm" fontWeight="medium">
                        Drop file here.
                      </Text>
                      {draggedFile && (
                        <Box
                          bg="white"
                          borderRadius="full"
                          px={4}
                          py={2}
                          border="1px solid"
                          borderColor="gray.400"
                          display="flex"
                          alignItems="center"
                          gap={3}
                          maxW="100%"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            color="gray.700"
                          >
                            <HiOutlineDocument size={20} />
                          </Box>
                          <Text
                            color="gray.900"
                            fontSize="sm"
                            fontWeight="medium"
                            noOfLines={1}
                            maxW="300px"
                          >
                            {draggedFile.name}
                          </Text>
                        </Box>
                      )}
                    </VStack>
                  ) : (
                    <VStack spacing={2}>
                      <Text color="gray.600" fontSize="sm">
                        Drag and drop files here or{" "}
                        <Link
                          color="blue.600"
                          textDecoration="underline"
                          fontWeight="medium"
                          _hover={{ color: "blue.700" }}
                        >
                          browse files
                        </Link>{" "}
                        on your computer.
                      </Text>
                      <HStack
                        spacing={1}
                        fontSize="xs"
                        color="gray.500"
                        justify="center"
                      >
                        <Text>Format: mp4, mov, avi, mkv, wmv, webm</Text>
                        <Text>•</Text>
                        <Text>Size: 1GB / 10 mins.</Text>
                      </HStack>
                    </VStack>
                  )}
                </Box>
              </VStack>
            )}
          </VStack>
        )}
      </VStack>

      {/* Recording Flow Modal */}
      <Modal isOpen={isRecordingModalOpen} onClose={handleExitSetup} size="xl">
        <ModalOverlay />
        <ModalContent
          maxW="960px"
          w="90%"
          mt="80px"
          mb="auto"
          borderRadius="24px"
          h="599px"
          maxH="calc(100vh - 80px)"
          display="flex"
          flexDirection="column"
        >
          {/* Modal Header */}
          <ModalHeader pt={6} pb={4} px={6} flexShrink={0}>
            <Flex justify="space-between" align="center" width="100%">
              <Text
                fontSize="24px"
                lineHeight="28px"
                fontWeight="semibold"
                color="gray.800"
              >
                {uploadState.type === "empty" && "Camera & microphone setup"}
                {uploadState.type === "hardware_setup" && "Hardware access"}
                {uploadState.type === "device_setup" &&
                  "Camera & microphone setup"}
                {(uploadState.type === "countdown" ||
                  uploadState.type === "recording") &&
                  "Record message"}
                {uploadState.type === "preview" && "Review recording"}
              </Text>
              <ModalCloseButton position="static" boxSize="32px" />
            </Flex>
          </ModalHeader>

          <ModalBody
            px={6}
            pt={6}
            pb={6}
            display="flex"
            flexDirection="column"
            flex={1}
            minH={0}
          >
            {/* Loading spinner while camera initializes */}
            {uploadState.type === "empty" && (
              <VStack spacing={4} flex={1} justify="center" align="center">
                <Spinner size="xl" color="blue.500" thickness="3px" />
                <Text color="gray.500" fontSize="sm">
                  Initializing camera...
                </Text>
              </VStack>
            )}

            {/* Hardware Setup Content */}
            {uploadState.type === "hardware_setup" && (
              <VStack spacing={6} py={8} flex={1} justify="center">
                {/* Icons: Camera + Plus + Microphone */}
                <HStack spacing={4} justify="center" align="center">
                  <Box color="gray.500">
                    <HiOutlineVideoCamera size={64} />
                  </Box>
                  <Text color="gray.500" fontSize="24px" fontWeight="bold">
                    +
                  </Text>
                  <Box color="gray.500">
                    <HiOutlineMicrophone size={64} />
                  </Box>
                </HStack>

                {/* Text */}
                <Text
                  color="gray.700"
                  fontSize="14px"
                  lineHeight="20px"
                  textAlign="center"
                  maxW="640px"
                >
                  To record your message, we need access to your camera and
                  microphone. Please accept the browser&apos;s request to access
                  your hardware when prompted.
                </Text>
              </VStack>
            )}

            {/* Device Setup Content */}
            {uploadState.type === "device_setup" && (
              <VStack spacing={8} py={4} flex={1} justify="center">
                <Text
                  color="gray.600"
                  fontSize="14px"
                  lineHeight="20px"
                  textAlign="center"
                >
                  Let&apos;s make sure we&apos;re using the correct camera and
                  microphone, and that your microphone is picking up sound.
                </Text>

                <Flex justify="center" width="100%" flex={1} align="center">
                  <HStack spacing={8} align="center">
                    {/* Left: Video preview */}
                    <Box
                      width="400px"
                      height="270px"
                      borderRadius="md"
                      overflow="hidden"
                      bg="black"
                      flexShrink={0}
                      position="relative"
                    >
                      <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    {/* Right: Device selection */}
                    <VStack spacing={4} align="flex-start" flexShrink={0}>
                      {/* Camera selection */}
                      <FormControl>
                        <Box position="relative" width="350px">
                          <Select
                            width="100%"
                            variant="leftIcon"
                            value={uploadState.selectedCameraId || ""}
                            onChange={(e) =>
                              handleDeviceChange("camera", e.target.value)
                            }
                            isDisabled={readOnly}
                            placeholder={
                              availableCameras.length === 0
                                ? "Loading cameras..."
                                : "Select camera"
                            }
                          >
                            {availableCameras.map((camera) => (
                              <option
                                key={camera.deviceId}
                                value={camera.deviceId}
                              >
                                {camera.label ||
                                  `Camera ${camera.deviceId.slice(0, 8)}`}
                              </option>
                            ))}
                          </Select>
                          <Icon
                            as={HiOutlineVideoCamera}
                            color="gray.500"
                            boxSize="5"
                            position="absolute"
                            left="2.5"
                            top="50%"
                            transform="translateY(-50%)"
                            pointerEvents="none"
                          />
                        </Box>
                      </FormControl>

                      {/* Microphone selection */}
                      <FormControl>
                        <Box position="relative" width="350px">
                          <Select
                            width="100%"
                            variant="leftIcon"
                            value={uploadState.selectedMicrophoneId || ""}
                            onChange={(e) =>
                              handleDeviceChange("microphone", e.target.value)
                            }
                            isDisabled={readOnly}
                            placeholder={
                              availableMicrophones.length === 0
                                ? "Loading microphones..."
                                : "Select microphone"
                            }
                          >
                            {availableMicrophones.map((mic) => (
                              <option key={mic.deviceId} value={mic.deviceId}>
                                {mic.label ||
                                  `Microphone ${mic.deviceId.slice(0, 8)}`}
                              </option>
                            ))}
                          </Select>
                          <Icon
                            as={HiOutlineMicrophone}
                            color="gray.500"
                            boxSize="5"
                            position="absolute"
                            left="2.5"
                            top="50%"
                            transform="translateY(-50%)"
                            pointerEvents="none"
                          />
                        </Box>

                        <AudioIndicator
                          volume={audioLevel * 100}
                          parentBg="white"
                          h="2"
                          mt="2"
                          width="350px"
                          numBars={8}
                          spacing={1}
                        />
                      </FormControl>
                    </VStack>
                  </HStack>
                </Flex>
              </VStack>
            )}

            {/* Countdown and Recording Content */}
            {(uploadState.type === "countdown" ||
              uploadState.type === "recording") &&
              previewStreamRef.current && (
                <VStack
                  spacing={4}
                  flex={1}
                  justify="center"
                  minH={0}
                  align="center"
                >
                  <HStack
                    spacing={3}
                    justify="space-between"
                    width="100%"
                    maxW="600px"
                    mx="auto"
                    flexShrink={0}
                    align="center"
                  >
                    <HStack
                      spacing={2}
                      px={3}
                      py={1.5}
                      borderRadius="full"
                      border="1px solid"
                      borderColor={
                        uploadState.type === "recording"
                          ? "red.200"
                          : "gray.200"
                      }
                      color={
                        uploadState.type === "recording"
                          ? "red.600"
                          : "gray.400"
                      }
                      bg="white"
                    >
                      <Box
                        width="8px"
                        height="8px"
                        borderRadius="full"
                        bg={
                          uploadState.type === "recording"
                            ? "red.500"
                            : "gray.300"
                        }
                      />
                      <Text fontSize="xs" fontWeight="medium">
                        Recording
                      </Text>
                    </HStack>
                    <MonospacedText
                      text={
                        uploadState.type === "recording"
                          ? formatDuration(recordingDuration)
                          : "00:00"
                      }
                      fontSize="sm"
                      fontWeight="medium"
                      color={
                        uploadState.type === "recording"
                          ? "gray.900"
                          : "gray.400"
                      }
                    />
                  </HStack>
                  <Box
                    width="100%"
                    maxW="600px"
                    mx="auto"
                    borderRadius="md"
                    overflow="hidden"
                    bg="black"
                    position="relative"
                    h="338px"
                    flexShrink={0}
                  >
                    <VideoPreview
                      stream={previewStreamRef.current}
                      videoRef={videoRef}
                    />
                    {/* Countdown overlay */}
                    {uploadState.type === "countdown" && (
                      <Box
                        position="absolute"
                        inset={0}
                        bg="blackAlpha.600"
                        borderRadius="md"
                        zIndex={5}
                        pointerEvents="none"
                      />
                    )}
                    {uploadState.type === "countdown" && (
                      <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        zIndex={10}
                        textAlign="center"
                        pointerEvents="none"
                      >
                        <Text fontSize="14px" lineHeight="20px" color="white">
                          Recording in...
                        </Text>
                        <Text
                          fontSize="48px"
                          lineHeight="56px"
                          fontWeight="semibold"
                          color="white"
                        >
                          {uploadState.countdown}
                        </Text>
                      </Box>
                    )}
                  </Box>
                  <AudioIndicator
                    volume={audioLevel * 100}
                    parentBg="white"
                    h="2"
                    width="100%"
                    maxW="600px"
                    mx="auto"
                    numBars={8}
                    spacing={1}
                    flexShrink={0}
                  />
                </VStack>
              )}
            {/* Preview Content */}
            {uploadState.type === "preview" && (
              <VStack
                spacing={4}
                flex={1}
                justify="center"
                minH={0}
                align="center"
              >
                {/* Spacer to match recording indicator/timer height */}
                <Box
                  width="100%"
                  maxW="600px"
                  mx="auto"
                  h="32px"
                  flexShrink={0}
                />
                <Box
                  width="100%"
                  maxW="600px"
                  mx="auto"
                  borderRadius="md"
                  overflow="hidden"
                  bg="black"
                  boxShadow="sm"
                  position="relative"
                  h="338px"
                  flexShrink={0}
                >
                  <video
                    src={uploadState.previewUrl}
                    controls
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onLoadedMetadata={(e) => {
                      const video = e.currentTarget;
                      if (video.duration > 0.5) {
                        video.currentTime = 0.5;
                      }
                    }}
                  />
                </Box>
                {/* Spacer to match soundbar height */}
                <Box
                  width="100%"
                  maxW="600px"
                  mx="auto"
                  h="8px"
                  flexShrink={0}
                />
              </VStack>
            )}
          </ModalBody>

          {/* Modal Footer */}
          <Box
            borderTop="1px solid"
            borderColor="gray.100"
            px={6}
            py={6}
            flexShrink={0}
          >
            {uploadState.type === "hardware_setup" && (
              <HStack justify="space-between" width="100%">
                <Button
                  variant="ghost"
                  onClick={handleExitSetup}
                  isDisabled={readOnly}
                  color="blue.600"
                  fontWeight="normal"
                >
                  Decline
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleRequestPermissions}
                  isDisabled={readOnly}
                >
                  Grant access
                </Button>
              </HStack>
            )}

            {uploadState.type === "device_setup" && (
              <HStack justify="space-between" width="100%">
                <Button
                  variant="ghost"
                  onClick={handleExitSetup}
                  isDisabled={readOnly}
                  color="blue.600"
                  fontWeight="normal"
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleStartRecordingFromDeviceSetup}
                  isDisabled={readOnly}
                >
                  Start recording
                </Button>
              </HStack>
            )}

            {uploadState.type === "countdown" && (
              <HStack justify="space-between" width="100%">
                <Button
                  variant="ghost"
                  onClick={handleExitSetup}
                  isDisabled={readOnly}
                  color="blue.600"
                  fontWeight="normal"
                >
                  Cancel
                </Button>
                <HStack spacing={4}>
                  <Button variant="outline" isDisabled>
                    Start Over
                  </Button>
                  <Button colorScheme="blue" justifyContent="center" isDisabled>
                    Stop Recording
                  </Button>
                </HStack>
              </HStack>
            )}

            {uploadState.type === "recording" && (
              <HStack justify="space-between" width="100%">
                <Button
                  variant="ghost"
                  onClick={handleExitSetup}
                  isDisabled={readOnly}
                  color="blue.600"
                  fontWeight="normal"
                >
                  Cancel
                </Button>
                <HStack spacing={4}>
                  <Button
                    variant="outline"
                    onClick={handleStartOver}
                    isDisabled={readOnly}
                  >
                    Start Over
                  </Button>
                  <Button
                    onClick={handleStopRecording}
                    colorScheme="blue"
                    justifyContent="center"
                    isDisabled={readOnly}
                  >
                    Stop Recording
                  </Button>
                </HStack>
              </HStack>
            )}

            {uploadState.type === "preview" && (
              <HStack justify="space-between" width="100%">
                <Button
                  variant="ghost"
                  onClick={handleExitSetup}
                  isDisabled={readOnly}
                  color="blue.600"
                  fontWeight="normal"
                >
                  Cancel
                </Button>
                <HStack spacing={4}>
                  <Button
                    variant="outline"
                    onClick={handleStartOver}
                    isDisabled={readOnly}
                  >
                    Start Over
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={() =>
                      handleConfirmUpload(
                        uploadState.recording,
                        uploadState.duration
                      )
                    }
                    isDisabled={readOnly}
                  >
                    Save recording
                  </Button>
                </HStack>
              </HStack>
            )}
          </Box>
        </ModalContent>
      </Modal>

      {uploadState.type === "uploading" && (
        <MediaFileCard
          status="uploading"
          fileName={
            lastUploadAttemptRef.current?.fileName ||
            "Interview Intro Recording"
          }
          fileType={lastUploadAttemptRef.current?.fileType || "mp4"}
          fileSizeBytes={lastUploadAttemptRef.current?.fileSizeBytes}
          progress={uploadState.progress}
          uploadSpeed={uploadState.uploadSpeed}
          onCancel={handleCancelUpload}
          readOnly={readOnly}
        />
      )}

      {uploadState.type === "processing" && (
        <MediaFileCard
          status="processing"
          fileName={
            lastUploadAttemptRef.current?.fileName ||
            "Interview Intro Recording"
          }
          fileType={lastUploadAttemptRef.current?.fileType || "mp4"}
          fileSizeBytes={lastUploadAttemptRef.current?.fileSizeBytes}
          readOnly={readOnly}
        />
      )}

      {uploadState.type === "uploaded" && (
        <MediaFileCard
          status="uploaded"
          fileName={uploadedFileName}
          fileType={uploadState.format}
          metaText={uploadedMetaText || undefined}
          mediaUrl={currentMediaUrl || undefined}
          onRemove={handleRemove}
          readOnly={readOnly}
          isRemoving={isRemoving}
        />
      )}

      {uploadState.type === "error" && (
        <MediaFileCard
          status="error"
          fileName={errorFileName}
          fileType={lastUploadAttemptRef.current?.fileType || "mp4"}
          metaText={errorMetaText || undefined}
          canRetry={uploadState.canRetry}
          onRetry={() => {
            if (uploadState.canRetry && lastUploadAttemptRef.current) {
              handleRetryUpload();
            } else {
              setUploadState({ type: "empty" });
              lastUploadAttemptRef.current = null;
            }
          }}
          onRemove={() => {
            setUploadState({ type: "empty" });
            lastUploadAttemptRef.current = null;
          }}
          readOnly={readOnly}
        />
      )}
    </FormControl>
  );
};

export default IntroMediaUpload;
